import React, { useEffect, useRef, useState } from "react";
import {
    AlertTriangle, Shield, RefreshCw, Search, ChevronUp, ChevronDown
} from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { Pagination } from "./Pagination";

// Dummy spinner
const LoadingSpinner = ({ section }: { section: string }) => (
    <div className="flex flex-col items-center justify-center p-12 space-y-4">
        <div className="w-8 h-8 border-2 border-yellow-500/20 border-t-yellow-500 rounded-full animate-spin"></div>
        <p className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">Loading {section}...</p>
    </div>
);

interface SystemIssuesProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const DEFAULT_PER_PAGE = 10;

const SystemIssues: React.FC<SystemIssuesProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [events, setEvents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination & Search state
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
    const [total, setTotal] = useState<number>(0);
    const [search, setSearch] = useState<string>("");
    const [searchInput, setSearchInput] = useState<string>("");
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sort State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>({ key: "event_time", direction: "desc" });

    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        debounceTimer.current = setTimeout(() => {
            const trimmed = value.trim();
            setSearch(trimmed);
            setPage(1);
            fetchIssues(0, perPage, trimmed, sortConfig);
        }, 400);
    };
    const fetchIssues = async (
        skip: number = 0,
        limit: number = perPage,
        searchTerm: string = search,
        currentSort = sortConfig
    ) => {
        setLoading(true);
        try {
            const queryObj: any = {
                client_id,
                organization_id,
                agent_id,
                asset_id,
            };

            // Basic search applied to Title or Message
            if (searchTerm) {
                queryObj.$or = [
                    { title: { $ilike: `%${searchTerm}%` } },
                    { message: { $ilike: `%${searchTerm}%` } },
                    { type: { $ilike: `%${searchTerm}%` } },
                    { severity: { $ilike: `%${searchTerm}%` } },
                    { category: { $ilike: `%${searchTerm}%` } },
                    { device: { $ilike: `%${searchTerm}%` } },
                    { event_time: { $ilike: `%${searchTerm}%` } },
                    { recommendation: { $ilike: `%${searchTerm}%` } },
                    { log_name: { $ilike: `%${searchTerm}%` } }

                ];
            }

            const params: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj),
            };

            if (currentSort) {
                params.sort_by = currentSort.key;
                params.sort_order = currentSort.direction;
            }

            const response = await baseapi.get("/asset_issues", { params });

            // Using standard data shape returned by the API
            setEvents(response.data?.data ?? []);
            setTotal(response.data?.total ?? 0);

        } catch (err: any) {
            showError(err.message);
            setEvents([]);
            setTotal(0);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchIssues(0, perPage, "", sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client_id, organization_id, agent_id, asset_id]);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= Math.ceil(total / perPage)) {
            setPage(newPage);
            fetchIssues((perPage * (newPage - 1)), perPage, search, sortConfig);
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setPerPage(value);
        setPage(1);
        fetchIssues(0, value, search, sortConfig);
    };

    const handleRefresh = () => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        const newPage = 1;
        const newPerPage = DEFAULT_PER_PAGE;
        const newSearch = "";
        const newSearchInput = "";
        setPage(newPage);
        setPerPage(newPerPage);
        setSearch(newSearch);
        setSearchInput(newSearchInput)
        fetchIssues((newPage - 1) * newPerPage, newPerPage, newSearch, sortConfig);
    };

    const handleSort = (key: string) => {
        let newDirection: "asc" | "desc" = "asc";
        if (sortConfig && sortConfig.key === key) {
            newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
        }
        const newSortConfig = { key, direction: newDirection };
        setSortConfig(newSortConfig);
        setPage(1); // Reset to page 1 on sort
        fetchIssues(0, perPage, search, newSortConfig);
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, widthClass }: { label: string; sortKey: string; widthClass?: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`${widthClass} px-3 py-2 text-left text-[10px] font-bold text-slate-700 dark:text-gray-200 uppercase tracking-wider border-b border-slate-200 dark:border-gray-700 cursor-pointer group select-none whitespace-nowrap hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors`}
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-yellow-600 dark:text-yellow-400" />
                            ) : (
                                <ChevronDown size={12} className="text-yellow-600 dark:text-yellow-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-slate-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    // Helper for date formatting
    const formatDate = (dateStr: string) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleString();
    };

    return (
        <div className="p-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-3 border-b border-slate-200 dark:border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <AlertTriangle className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">System Issues</h2>
                        <p className="text-slate-500 dark:text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {total || '...'} issues found
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="p-2 bg-white dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>

                    <div className="relative">
                        <Search className="w-3.5 h-3.5 text-slate-400 dark:text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={searchInput}
                            onChange={handleSearchChange}
                            placeholder="Search events..."
                            className="w-64 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-8 shadow-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-white dark:bg-gray-900/40 rounded-md border border-slate-200 dark:border-gray-700 overflow-hidden flex flex-col relative shadow-sm">
                <div className="flex-1 overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse min-w-[1000px]">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr>
                                <SortableHeader widthClass="w-[12%]" label="Time" sortKey="event_time" />
                                <SortableHeader widthClass="w-[8%]" label="Severity" sortKey="severity" />
                                <SortableHeader widthClass="w-[12%]" label="Category" sortKey="category" />
                                <SortableHeader widthClass="w-[15%]" label="Device / Source" sortKey="device" />
                                <SortableHeader widthClass="w-[53%]" label="Event Details" sortKey="message" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                            {loading ? (
                                <tr>
                                    <td colSpan={5}>
                                        <LoadingSpinner section="system events" />
                                    </td>
                                </tr>
                            ) : events.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center text-slate-400 dark:text-gray-500 font-bold uppercase tracking-widest italic">
                                        No issues found
                                    </td>
                                </tr>
                            ) : (
                                events.map((event, idx) => (
                                    <tr key={event.id ?? idx} className="hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors duration-100 group">
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-300 font-mono whitespace-nowrap align-top">
                                            {formatDate(event.event_time)}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${event.severity === "CRITICAL" ? "text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10" :
                                                event.severity === "WARNING" ? "text-amber-700 dark:text-yellow-400 border-amber-200 dark:border-yellow-500/20 bg-amber-50 dark:bg-yellow-500/10" :
                                                    "text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/20 bg-blue-50 dark:bg-blue-500/10"
                                                }`}>
                                                {event.severity || "INFO"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-[10px] text-slate-800 dark:text-gray-200 font-semibold align-top truncate" title={event.category}>
                                            {event.category || "-"}
                                        </td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-300 align-top truncate" title={event.device}>
                                            {event.device || "-"}
                                        </td>
                                        <td className="px-3 py-2 text-[11px] text-slate-700 dark:text-gray-200 align-top">
                                            <div className="font-bold text-slate-900 dark:text-white mb-0.5" title={event.title}>{event.title || "Event Log"}</div>
                                            <div className="text-slate-500 dark:text-gray-400 line-clamp-2 leading-relaxed" title={event.message}>{event.message || "-"}</div>
                                            {event.recommendation && (
                                                <div className="mt-1 text-blue-600 dark:text-blue-400/80 text-[10px] italic flex items-center">
                                                    <Shield className="w-3 h-3 mr-1" />
                                                    {event.recommendation}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot className="bg-slate-50/50 dark:bg-gray-900/60 sticky bottom-0">
                            <tr>
                                <td colSpan={5} className="p-0">
                                    <Pagination
                                        currentPage={page}
                                        totalPages={Math.max(1, Math.ceil(total / perPage))}
                                        onPageChange={handlePageChange}
                                        itemsPerPage={perPage}
                                        totalItems={total}
                                        onItemsPerPageChange={handleItemsPerPageChange}
                                    />
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SystemIssues;